import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { z } from 'zod';
import { notifySuccess } from '#/utils/NotifySuccess.tsx';
import {
  getDisplayErrorMessage,
  getResponseStatus,
} from '#/service/driver-management/utils/apiError.ts';
import { requiredText } from '#/service/driver-management/utils/formValidation.ts';
import { useDeleteAccount } from '../hooks/useAccountData.ts';
import { IconAlertCircle } from '@tabler/icons-react';

const CONFIRM_TEXT = 'DELETE';

const getDeleteErrorMessage = (error: Error) => {
  switch (getResponseStatus(error)) {
    case 403:
      return '你沒有刪除帳號的權限，請聯繫管理員';
    case 500:
      return '刪除未完成，請重試';
    default:
      return `刪除失敗：${getDisplayErrorMessage(error)}`;
  }
};

type FormValues = {
  reason: string;
  confirmText: string;
};

type DeleteAccountModalProps = {
  opened: boolean;
  accountId: string;
  email: string;
  onClose: () => void;
};

const DeleteAccountFormSchema = z.object({
  reason: requiredText('請輸入刪除原因'),
  confirmText: z.literal(CONFIRM_TEXT, '請輸入 DELETE 以確認刪除'),
});

const DeleteAccountModal = ({
  opened,
  accountId,
  email,
  onClose,
}: DeleteAccountModalProps) => {
  const navigate = useNavigate();
  const { mutate, isPending, error, reset } = useDeleteAccount();

  const form = useForm<FormValues, z.infer<typeof DeleteAccountFormSchema>>({
    initialValues: {
      reason: '',
      confirmText: '',
    },
    validate: schemaResolver(DeleteAccountFormSchema, { sync: true }),
    transformValues: (values) => DeleteAccountFormSchema.parse(values),
  });

  const handleClose = () => {
    form.reset();
    reset();
    onClose();
  };

  const handleSubmit = (values: z.infer<typeof DeleteAccountFormSchema>) => {
    mutate(
      { id: accountId, reason: values.reason },
      {
        onSuccess: () => {
          notifySuccess('帳號已刪除', email);
          navigate('/driver-management/drivers');
        },
      },
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      closeOnClickOutside={!isPending}
      closeOnEscape={!isPending}
      withCloseButton={!isPending}
      title="刪除帳號"
      centered
      styles={{ title: { fontWeight: 700, fontSize: 16 } }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Text size="sm">確定要刪除 {email} 嗎？此操作無法復原。</Text>

          <TextInput
            label="刪除原因"
            placeholder="例：用戶要求"
            withAsterisk
            {...form.getInputProps('reason')}
          />

          <TextInput
            label={`請輸入 ${CONFIRM_TEXT} 確認刪除`}
            placeholder={CONFIRM_TEXT}
            withAsterisk
            {...form.getInputProps('confirmText')}
          />

          {error && (
            <Alert color="red" variant="light" icon={<IconAlertCircle />}>
              {getDeleteErrorMessage(error)}
            </Alert>
          )}

          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={handleClose}
              disabled={isPending}
            >
              取消
            </Button>
            <Button color="red" type="submit" loading={isPending}>
              確認刪除
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export { DeleteAccountModal };
